import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { ScrollText } from 'lucide-react';
import type { SystemLogToken } from '@sleed/bizcommerce-api';
import { SystemGrid, type SystemGridColumn } from '~/components/system/grid/system-grid';
import { formatDateTime } from '~/lib/format-date';
import { useSystemLogList } from '~/hooks/use-system-log';

/** System log — read-only audit grid (no add/edit/delete). */
export function SystemLogGridView() {
	const { t } = useTranslation();
	const { data, isLoading, isError, refetch } = useSystemLogList();

	const columns = useMemo<SystemGridColumn<SystemLogToken>[]>(
		() => [
			{
				key: 'date',
				header: t('system.systemLog.columns.date'),
				width: '160px',
				sortValue: l => (l.edt ? String(l.edt) : ''),
				exportValue: l => formatDateTime(l.edt),
				render: l => <span className="truncate text-xs tabular-nums text-foreground-muted">{formatDateTime(l.edt)}</span>
			},
			{
				key: 'level',
				header: t('system.systemLog.columns.level'),
				width: '80px',
				align: 'center',
				sortValue: l => l.lv ?? 0,
				exportValue: l => l.lv ?? 0,
				render: l => (
					<span className="rounded bg-muted px-1.5 py-0.5 text-[10px] font-medium tabular-nums text-foreground-muted">{l.lv ?? 0}</span>
				)
			},
			{
				key: 'module',
				header: t('system.systemLog.columns.module'),
				width: '160px',
				sortValue: l => l.mod ?? '',
				exportValue: l => l.mod ?? '',
				render: l => (
					<span className="truncate text-xs text-foreground-muted" title={l.mod ?? ''}>
						{l.mod || '—'}
					</span>
				)
			},
			{
				key: 'message',
				header: t('system.systemLog.columns.message'),
				width: 'minmax(0,1fr)',
				sortValue: l => l.msg,
				exportValue: l => l.msg,
				render: l => (
					<span className="truncate text-sm text-foreground" title={l.msg}>
						{l.msg}
					</span>
				)
			},
			{
				key: 'raw',
				header: t('system.systemLog.columns.raw'),
				width: '220px',
				exportValue: l => l.rdt ?? '',
				render: l => (
					<span className="truncate font-mono text-[11px] text-foreground-subtle" title={l.rdt ?? ''}>
						{l.rdt || '—'}
					</span>
				)
			}
		],
		[t]
	);

	return (
		<SystemGrid<SystemLogToken>
			gridId="system-log"
			title={t('system.systemLog.title')}
			rows={data ?? []}
			loading={isLoading}
			error={isError}
			onRetry={() => void refetch()}
			getRowId={l => l.id ?? `${l.edt}-${l.msg}`}
			columns={columns}
			searchPredicate={(l, q) => `${l.msg ?? ''} ${l.mod ?? ''} ${l.rdt ?? ''}`.toLowerCase().includes(q)}
			defaultSort={{ key: 'date', dir: 'desc' }}
			emptyIcon={<ScrollText size={36} className="text-foreground-disabled" strokeWidth={1.25} />}
			exportFilename="system-log"
		/>
	);
}
